import {
  isRootOrganizationServiceVariant,
  readServiceVariantIdentity,
  isTenantOrganizationServiceVariant,
} from '@/lib/service-variant-types'
import { orgTreeApi, type OrgTreeServiceInstance } from '@/services/org-tree'

function getServiceVariantId(instance: OrgTreeServiceInstance | null | undefined): string {
  const identity = readServiceVariantIdentity(instance)
  return String(identity.uniqueId ?? '').trim()
}

function getServiceVariantName(instance: OrgTreeServiceInstance | null | undefined): string {
  const identity = readServiceVariantIdentity(instance)
  return String(identity.label ?? '').trim()
}

async function walkServiceInstanceChain(
  workspaceServiceInstanceId: string,
  matcher: (instance: OrgTreeServiceInstance) => boolean,
): Promise<OrgTreeServiceInstance | null> {
  const visited = new Set<string>()
  let currentId = String(workspaceServiceInstanceId ?? '').trim()
  let current: OrgTreeServiceInstance | null = null

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId)
    current = await orgTreeApi.getOptionalServiceInstance(currentId)
    if (!current) {
      return null
    }

    if (matcher(current)) {
      return current
    }

    const parentId = String(current.parent_instance_id ?? '').trim()
    if (!parentId) {
      break
    }
    currentId = parentId
  }

  return null
}

export async function resolveTenantServiceInstance(
  workspaceServiceInstanceId: string,
): Promise<OrgTreeServiceInstance | null> {
  return walkServiceInstanceChain(
    workspaceServiceInstanceId,
    (instance) => isTenantOrganizationServiceVariant(getServiceVariantId(instance), getServiceVariantName(instance)),
  )
}

export async function resolveOrganizationServiceInstance(
  workspaceServiceInstanceId: string,
): Promise<OrgTreeServiceInstance | null> {
  return walkServiceInstanceChain(
    workspaceServiceInstanceId,
    (instance) => isRootOrganizationServiceVariant(getServiceVariantId(instance), getServiceVariantName(instance)),
  )
}

export async function resolveTenantServiceInstanceOwnerUserId(
  workspaceServiceInstanceId: string,
): Promise<string | null> {
  const tenantInstance = await resolveTenantServiceInstance(workspaceServiceInstanceId)
  const ownerFallback = (tenantInstance as Record<string, unknown> | null)?.owner
  const ownerUserId = String(tenantInstance?.owner_user_id ?? ownerFallback ?? '').trim()
  return ownerUserId || null
}

/** @deprecated Prefer resolveTenantServiceInstanceOwnerUserId for tenant-scoped flows. */
export async function resolveOrganizationServiceInstanceOwnerUserId(
  workspaceServiceInstanceId: string,
): Promise<string | null> {
  return resolveTenantServiceInstanceOwnerUserId(workspaceServiceInstanceId)
}

export function getTenantOwnerDisplayName(
  tenantInstance: OrgTreeServiceInstance | null | undefined,
): string {
  const profileName = String(tenantInstance?.owner_profile?.name ?? '').trim()
  if (profileName) {
    return profileName
  }

  return String(tenantInstance?.owner_name ?? '').trim()
}

/** @deprecated Prefer getTenantOwnerDisplayName for tenant-scoped flows. */
export function getOrganizationOwnerDisplayName(
  organizationInstance: OrgTreeServiceInstance | null | undefined,
): string {
  return getTenantOwnerDisplayName(organizationInstance)
}
